import { createTheme, alpha, type PaletteOptions } from '@mui/material/styles';
// Lets the theme's `components` type know about the DataGrid slot.
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type { ResolvedColorMode } from './colorMode';

/**
 * Brand identity derived from the AST logo (ast-logo-vectorized.svg): an earthy,
 * archaeological palette built on the logo's two browns — a deep espresso brown
 * (#5a4c3d) and a lighter taupe (#7a6a5a) — over a warm, paper-like cream ground.
 */
const BRAND_BROWN = '#5a4c3d';
const BRAND_TAUPE = '#7a6a5a';

// Archivo (bundled via @fontsource) for titles, falling back to the system sans.
const TITLE_FONT = '"Archivo", "Segoe UI", Roboto, Arial, sans-serif';

const lightPalette: PaletteOptions = {
    mode: 'light',
    primary: {
        main: BRAND_BROWN,
        light: BRAND_TAUPE,
        dark: '#3a2f24',
        contrastText: '#ffffff'
    },
    secondary: {
        // A warm terracotta/ochre accent — earthy but distinct from the
        // brown, so secondary actions and highlights read clearly.
        main: '#b07d4b',
        light: '#cda077',
        dark: '#835729',
        contrastText: '#ffffff'
    },
    background: {
        // Warm off-white (echoes the logo's cream ground) instead of MUI's
        // cool grey, with plain white paper for cards/dialogs/grids.
        default: '#f6f2ec',
        paper: '#ffffff'
    },
    text: {
        primary: '#2e2820',
        secondary: '#6b5f52'
    },
    divider: 'rgba(90, 76, 61, 0.16)'
};

/**
 * The same identity after dark. The brand browns are too close to a dark ground
 * to carry text or fills, so they are LIFTED into tans (same hue, higher
 * luminance) and take dark text. The grounds stay warm rather than neutral grey.
 */
const darkPalette: PaletteOptions = {
    mode: 'dark',
    primary: {
        main: '#c2a883',
        light: '#d9c4a5',
        dark: '#9c8360',
        contrastText: '#241e17'
    },
    secondary: {
        main: '#d9a066',
        light: '#eab98a',
        dark: '#b07d4b',
        contrastText: '#241e17'
    },
    background: {
        // Warm near-black, with a slightly lifted surface for
        // cards/dialogs/grids — the counterpart of cream over white.
        default: '#1c1815',
        paper: '#26211c'
    },
    text: {
        primary: '#ede7de',
        secondary: '#b3a798'
    },
    divider: 'rgba(237, 231, 222, 0.16)'
};

/**
 * Scrollbars in the app's own colours: the browser default is a cool grey that
 * sits badly on the warm grounds, and after dark a light-grey bar is the
 * brightest thing on screen. Track a shade off its ground, thumb in the divider
 * tone. Deliberately applied to `*` — the app scrolls in many places.
 */
function scrollbarStyles(mode: ResolvedColorMode) {
    const dark = mode === 'dark';
    const track = dark ? '#221d19' : '#efe9e1';
    const thumb = dark ? alpha('#ede7de', 0.22) : alpha(BRAND_BROWN, 0.28);
    const thumbHover = dark ? alpha('#ede7de', 0.38) : alpha(BRAND_BROWN, 0.45);
    return {
        // Firefox: the standard two-value form, no pseudo-elements.
        '*': {
            scrollbarWidth: 'thin' as const,
            scrollbarColor: `${thumb} ${track}`
        },
        // WebKit/Blink: the pseudo-elements, for the hover states the standard
        // form does not cover.
        '*::-webkit-scrollbar': {
            width: 12,
            height: 12
        },
        '*::-webkit-scrollbar-track': {
            backgroundColor: track
        },
        '*::-webkit-scrollbar-thumb': {
            backgroundColor: thumb,
            borderRadius: 8,
            // An inset border leaves the thumb floating in its track.
            border: `3px solid ${track}`,
            backgroundClip: 'padding-box'
        },
        '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: thumbHover
        },
        // Where a vertical and a horizontal bar meet — grey by default.
        '*::-webkit-scrollbar-corner': {
            backgroundColor: track
        }
    };
}

/**
 * The ground of ONE row of a banded repeater — the Lagen of a baulicher Bestand,
 * the Besuche of a Tagebuch, and whatever list follows them.
 *
 * Meant as a step ABOVE `background.paper`, like a raised tray — which the two
 * modes cannot do symmetrically. Dark paper (#26211c) has room above it; light
 * paper is pure WHITE, so the light band goes downwards instead, landing between
 * the page ground (#f6f2ec, which read as a hole) and white. Both were dialled
 * in by eye against filled inputs; this is the one place to adjust them.
 */
export const REPEATER_BAND: Record<ResolvedColorMode, string> = {
    light: '#f8f5ef',
    dark: '#2f2922'
};

/**
 * The app theme in one appearance. Everything but the palette is shared, so the
 * two modes can only differ in colour — see LocalizedThemeProvider, which layers
 * MUI's locale bundles on top of this.
 */
export function createAppTheme(mode: ResolvedColorMode) {
    return createTheme({
        palette: mode === 'dark' ? darkPalette : lightPalette,
        shape: {
            borderRadius: 8
        },
        typography: {
            button: {
                textTransform: 'none',
                fontWeight: 600
            },
            h1: { fontFamily: TITLE_FONT },
            h2: { fontFamily: TITLE_FONT },
            h3: { fontFamily: TITLE_FONT },
            h4: { fontFamily: TITLE_FONT },
            h5: { fontFamily: TITLE_FONT, fontWeight: 600 },
            h6: { fontFamily: TITLE_FONT, fontWeight: 600 }
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: scrollbarStyles(mode)
            },
            MuiTextField: {
                // Filled inputs suit the warm, document-like forms.
                defaultProps: { variant: 'filled' }
            },
            MuiAppBar: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    /**
                     * A hairline under the bar instead of a shadow. From the
                     * divider token, not a fixed black, which would disappear
                     * under the dark bar.
                     */
                    root: ({ theme }) => ({
                        borderBottom: `1px solid ${theme.palette.divider}`
                    })
                }
            },
            MuiButton: {
                defaultProps: { disableElevation: true }
            },
            MuiDialogContent: {
                /**
                 * Every dialog rules its body off from the title and the buttons:
                 * a scrolling dialog needs the lines to say where the scrolling
                 * starts, and a short one looks like a different kind of window
                 * without them. Set ONCE here, not at two dozen call sites; a
                 * dialog that genuinely wants none passes `dividers={false}`.
                 */
                defaultProps: { dividers: true }
            },
            MuiAutocomplete: {
                /**
                 * Small chips throughout: at the default size a field drew medium
                 * ones beside the small ones in grid cells, which read as two
                 * kinds of value. Via the `chip` slot rather than `size`, which
                 * would shrink the whole input. A list drawing its own chips
                 * through `renderValue` passes `size="small"` itself.
                 */
                defaultProps: { slotProps: { chip: { size: 'small' } } }
            },
            MuiAlert: {
                styleOverrides: {
                    /**
                     * ONE kind of alert across the app: the tinted `standard`
                     * background PLUS an outline in its severity colour — the tint
                     * alone is easy to miss on the warm page ground. Only
                     * `standard` is touched; `filled` already carries its colour
                     * and `outlined` is the variant this would duplicate.
                     */
                    standard: ({ theme, ownerState }) => ({
                        // `color || severity`, the same resolution Alert uses for
                        // its own tint, so the two can never diverge.
                        border: `1px solid ${
                            theme.palette[
                                ownerState.color ??
                                    ownerState.severity ??
                                    'success'
                            ].main
                        }`
                    })
                }
            },
            MuiDataGrid: {
                styleOverrides: {
                    /**
                     * The textarea a `longText` column is edited in: MUI gives it
                     * no COLOUR, so it falls back to the browser's `fieldtext` —
                     * black on the dark paper behind it.
                     *
                     * Its own override rather than a selector under `root`, because
                     * the textarea lives in a Popper that MUI renders in a PORTAL,
                     * outside the grid's DOM. `inherit` rather than the token, so
                     * it stays right if MUI ever gives the panel another ground.
                     */
                    editLongTextCellTextarea: { color: 'inherit' },
                    root: ({ theme }) => ({
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600
                        },
                        /**
                         * A failed edit value gets an error tint + text colour, not an
                         * outline: the cell is focused while editing and its focus
                         * outline would hide one. The edit components flag the input
                         * with `Mui-error`, which `:has()` lifts onto the whole cell.
                         */
                        '& .MuiDataGrid-cell--editing:has(.Mui-error)': {
                            backgroundColor: alpha(
                                theme.palette.error.main,
                                0.12
                            ),
                            color: theme.palette.error.main
                        },
                        // Opt-in bold cells (columns set cellClassName), for a
                        // grid's key column — e.g. the Fundzettelnummer.
                        '& .grid-cell-bold': {
                            fontWeight: 600
                        },
                        /**
                         * A filtered column says so in its header funnel, in the
                         * same info tone as the toolbar's Filter button (see
                         * ActiveToolButton): "rows are missing" is one colour
                         * wherever it is said. MUI leaves the icon grey.
                         */
                        '& .MuiDataGrid-filterIcon': {
                            color: theme.palette.info.main
                        },
                        // Row-action buttons sit at the right edge of their cell —
                        // centred icons float oddly next to the scrollbar.

                        '& .MuiDataGrid-cell:has(.MuiDataGrid-actionsCell)': {
                            justifyContent: 'flex-end'
                        }
                    })
                }
            }
        }
    });
}
